import { Menu } from "antd";
import type { MenuProps } from "antd";
import Sider from "antd/lib/layout/Sider";
import logo from "assets/images/logo.png";
import { observer } from "mobx-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import type { Route } from "router";
import { settings } from "settings";
import { permissionStore } from "store/permissionStore";
import { userStore } from "store/userStore";
import "../styles/Sidebar.scss";

export const SIDENAV_WIDTH = 245;
export const SIDENAV_WIDTH_COLLAPSED = 75;

const buildMenuItems = (routes: Route[]): MenuProps["items"] =>
  routes
    .filter(
      (route) =>
        (route.checkIsDev ? userStore.info.isDev : true) &&
        !route.hidden &&
        (route.isAccess || !settings.checkPermission),
    )
    .map((route) => {
      const children = route.children?.filter(
        (child) => child.isAccess || !settings.checkPermission,
      );

      if (children?.length) {
        return {
          key: route.path!,
          icon: route.icon,
          label: route.title,
          children: children.map((item) => ({
            key: `${route.path}/${item.path}`,
            icon: item.icon,
            label: <Link to={`${route.path}/${item.path}`}>{item.title}</Link>,
          })),
        };
      }

      return {
        key: route.path!,
        icon: route.icon,
        label: <Link to={route.path || ""}>{route.title}</Link>,
      };
    });

type SidebarMenuProps = {
  collapsed: boolean;
  onNavigate?: () => void;
};

export const SidebarMenu = observer(
  ({ collapsed, onNavigate }: SidebarMenuProps) => {
    const location = useLocation();
    const [openKeys, setOpenKeys] = useState<string[]>([]);
    const [isLoaded, setIsLoaded] = useState(false);

    const menuItems = useMemo(
      () => buildMenuItems(permissionStore.accessRoutes),
      [permissionStore.accessRoutes, userStore.info.isDev],
    );

    useEffect(() => {
      const routes = permissionStore.accessRoutes;
      const parentRoute = routes.find(
        (route) =>
          route.children &&
          route.path &&
          location.pathname.startsWith(route.path),
      );

      if (parentRoute?.path) {
        setOpenKeys([parentRoute.path]);
      }

      setIsLoaded(true);
    }, [location.pathname]);

    const handleOpenChange: MenuProps["onOpenChange"] = (keys) => {
      setOpenKeys(keys as string[]);
    };

    const handleMenuClick: MenuProps["onClick"] = () => {
      onNavigate?.();
    };

    return (
      <div className="admin-sidebar__inner">
        <div className="logo-box sticky top-0 flex items-center justify-center px-6 py-3 backdrop-blur-xs">
          <Link to="/" onClick={onNavigate}>
            <img
              src={logo}
              className={collapsed ? "logo-sm" : "logo-lg"}
              alt="Logo"
            />
          </Link>
        </div>

        {isLoaded && (
          <Menu
            className="sidebar"
            theme="light"
            mode="inline"
            inlineCollapsed={collapsed}
            selectedKeys={[location.pathname]}
            openKeys={openKeys}
            onOpenChange={handleOpenChange}
            onClick={handleMenuClick}
            items={menuItems}
          />
        )}
      </div>
    );
  },
);

export const Sidebar = observer(({ collapsed }: { collapsed: boolean }) => {
  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      className="admin-sidebar admin-sidebar--desktop"
      width={SIDENAV_WIDTH}
      collapsedWidth={SIDENAV_WIDTH_COLLAPSED}
    >
      <SidebarMenu collapsed={collapsed} />
    </Sider>
  );
});
